import React, { useState } from 'react';
import { 
  TestTube, Users, Calendar, TrendingUp,
  BarChart3, Target, Clock, Settings
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';

interface PlanogramPattern {
  id: string;
  name: string;
  description: string;
  expectedSalesIncrease: number;
  expectedProfitIncrease: number;
  changes: number;
  confidence: number;
  rules: string[];
}

interface Store {
  id: string;
  name: string;
}

interface TestPlan {
  id: string;
  name: string;
  pattern: PlanogramPattern;
  testStores: string[];
  controlStores: string[];
  duration: number; // days
  startDate: string;
  status: 'draft' | 'active' | 'completed';
  metrics: {
    name: string;
    target: number;
    current?: number;
  }[];
}

interface ABTestPlannerProps {
  patterns: PlanogramPattern[];
  stores: Store[];
}

const ABTestPlanner: React.FC<ABTestPlannerProps> = ({ patterns, stores }) => {
  const [selectedPattern, setSelectedPattern] = useState<string>(patterns[0]?.id || '');
  const [testDuration, setTestDuration] = useState(14);
  const [selectedTestStores, setSelectedTestStores] = useState<string[]>([]);
  const [selectedControlStores, setSelectedControlStores] = useState<string[]>([]);
  
  // 既存のテスト計画（モック）
  const [testPlans] = useState<TestPlan[]>([
    {
      id: 'TEST001',
      name: '売上最大化パターン検証',
      pattern: patterns[0],
      testStores: ['shibuya', 'shinjuku'],
      controlStores: ['ginza'],
      duration: 14,
      startDate: '2025-09-25',
      status: 'active',
      metrics: [
        { name: '売上高', target: 15, current: 12.3 },
        { name: '客単価', target: 10, current: 8.5 },
        { name: '購買点数', target: 5, current: 6.2 }
      ]
    },
    {
      id: 'TEST002',
      name: '利益重視パターン検証',
      pattern: patterns[1],
      testStores: ['yokohama'],
      controlStores: ['chiba'],
      duration: 21,
      startDate: '2025-10-01',
      status: 'draft',
      metrics: [
        { name: '粗利率', target: 18, current: undefined },
        { name: '在庫回転率', target: 10, current: undefined }
      ]
    }
  ]);
  
  const handleStoreToggle = (storeId: string, type: 'test' | 'control') => {
    if (type === 'test') {
      setSelectedTestStores(prev => 
        prev.includes(storeId) 
          ? prev.filter(id => id !== storeId)
          : [...prev, storeId]
      );
      // テスト店舗に追加したらコントロール店舗から除外
      setSelectedControlStores(prev => prev.filter(id => id !== storeId));
    } else {
      setSelectedControlStores(prev => 
        prev.includes(storeId) 
          ? prev.filter(id => id !== storeId)
          : [...prev, storeId]
      );
      // コントロール店舗に追加したらテスト店舗から除外
      setSelectedTestStores(prev => prev.filter(id => id !== storeId));
    }
  };
  
  const calculateSampleSize = () => {
    // 統計的有意性を保つためのサンプルサイズ計算（簡易版）
    const effectSize = 0.5; // 中程度の効果量
    const power = 0.8; // 検出力80%
    const alpha = 0.05; // 有意水準5%
    
    // 簡易計算式
    const n = Math.ceil(15.7 / (effectSize * effectSize));
    return n;
  };

  const allStores = [
    { id: 'shibuya', name: '渋谷店', area: '都心', sales: 'A' },
    { id: 'shinjuku', name: '新宿店', area: '都心', sales: 'A' },
    { id: 'ginza', name: '銀座店', area: '都心', sales: 'A' },
    { id: 'yokohama', name: '横浜店', area: '郊外', sales: 'B' },
    { id: 'chiba', name: '千葉店', area: '郊外', sales: 'B' },
    { id: 'omiya', name: '大宮店', area: '郊外', sales: 'C' }
  ];

  return (
    <div className="space-y-6">
      {/* テスト作成エリア */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
          新規ABテスト計画
        </h3>
        
        <div className="space-y-4">
          {/* パターン選択 */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
              検証パターン
            </label>
            <select 
              className="w-full p-2 border rounded-lg"
              style={{ borderColor: colors.ui.border }}
              value={selectedPattern}
              onChange={(e) => setSelectedPattern(e.target.value)}
            >
              {patterns.map(pattern => (
                <option key={pattern.id} value={pattern.id}>
                  {pattern.name} (売上+{pattern.expectedSalesIncrease}%, 利益+{pattern.expectedProfitIncrease}%)
                </option>
              ))}
            </select>
          </div>
          
          {/* テスト期間 */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
              テスト期間
            </label>
            <div className="flex items-center gap-4">
              <input 
                type="range" 
                min="7" 
                max="30" 
                value={testDuration}
                onChange={(e) => setTestDuration(Number(e.target.value))}
                className="flex-1"
              />
              <span className="font-medium w-16">{testDuration}日間</span>
            </div>
            <p className="text-xs mt-1" style={{ color: colors.text.secondary }}>
              推奨: 14日以上（週次変動を2サイクル含む）
            </p>
          </div>
          
          {/* 店舗選択 */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
              対象店舗選択
            </label>
            
            <div className="grid grid-cols-2 gap-4">
              {/* テスト店舗 */}
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <TestTube size={16} style={{ color: colors.accent.blue }} />
                  テスト店舗（新パターン適用）
                </h4>
                <div className="space-y-2 p-3 border rounded-lg" style={{ borderColor: colors.ui.border }}>
                  {allStores.map(store => (
                    <label key={store.id} className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={selectedTestStores.includes(store.id)}
                        onChange={() => handleStoreToggle(store.id, 'test')}
                      />
                      <span className="text-sm">{store.name}</span>
                      <Badge variant="secondary" size="small">{store.area}</Badge>
                      <Badge variant="secondary" size="small">売上{store.sales}</Badge>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* コントロール店舗 */}
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <BarChart3 size={16} style={{ color: colors.text.secondary }} />
                  コントロール店舗（現状維持）
                </h4>
                <div className="space-y-2 p-3 border rounded-lg" style={{ borderColor: colors.ui.border }}>
                  {allStores.map(store => (
                    <label key={store.id} className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={selectedControlStores.includes(store.id)}
                        onChange={() => handleStoreToggle(store.id, 'control')}
                      />
                      <span className="text-sm">{store.name}</span>
                      <Badge variant="secondary" size="small">{store.area}</Badge>
                      <Badge variant="secondary" size="small">売上{store.sales}</Badge>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            {/* 統計的検証情報 */}
            <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <div className="flex items-center gap-2 mb-2">
                <Settings size={16} style={{ color: colors.accent.purple }} />
                <span className="font-medium text-sm">統計的検証要件</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span style={{ color: colors.text.secondary }}>必要サンプル数</span>
                  <div className="font-medium">{calculateSampleSize()}店舗</div>
                </div>
                <div>
                  <span style={{ color: colors.text.secondary }}>現在の選択</span>
                  <div className="font-medium">
                    {selectedTestStores.length + selectedControlStores.length}店舗
                  </div>
                </div>
                <div>
                  <span style={{ color: colors.text.secondary }}>検出力</span>
                  <div className="font-medium">
                    {selectedTestStores.length >= 2 && selectedControlStores.length >= 1 ? '80%' : '不足'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <Button 
            variant="primary"
            disabled={selectedTestStores.length < 1 || selectedControlStores.length < 1}
          >
            テスト計画を作成
          </Button>
        </div>
      </Card>
      
      {/* 実行中のテスト */}
      <Card>
        <div className="p-4 border-b" style={{ borderColor: colors.ui.border }}>
          <h3 className="font-semibold" style={{ color: colors.text.primary }}>
            テスト実行状況
          </h3>
        </div>
        
        <div className="p-4 space-y-4">
          {testPlans.map(plan => (
            <div 
              key={plan.id}
              className="border rounded-lg p-4"
              style={{ borderColor: colors.ui.border }}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium" style={{ color: colors.text.primary }}>
                      {plan.name}
                    </h4>
                    <Badge 
                      variant={plan.status === 'active' ? 'success' : 
                              plan.status === 'completed' ? 'secondary' : 'warning'}
                    >
                      {plan.status === 'active' ? '実行中' : 
                       plan.status === 'completed' ? '完了' : 'ドラフト'}
                    </Badge>
                  </div>
                  <p className="text-sm" style={{ color: colors.text.secondary }}>
                    {plan.pattern.description}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm" style={{ color: colors.text.secondary }}>
                    {new Date(plan.startDate).toLocaleDateString('ja-JP')}
                  </div>
                  <div className="text-xs" style={{ color: colors.text.secondary }}>
                    {plan.duration}日間
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <span className="text-xs" style={{ color: colors.text.secondary }}>テスト店舗</span>
                  <div className="flex gap-1 mt-1">
                    {plan.testStores.map(storeId => {
                      const store = allStores.find(s => s.id === storeId);
                      return (
                        <Badge key={storeId} variant="primary" size="small">
                          {store?.name}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <span className="text-xs" style={{ color: colors.text.secondary }}>コントロール店舗</span>
                  <div className="flex gap-1 mt-1">
                    {plan.controlStores.map(storeId => {
                      const store = allStores.find(s => s.id === storeId);
                      return (
                        <Badge key={storeId} variant="secondary" size="small">
                          {store?.name}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              {/* メトリクス */}
              {plan.status === 'active' && (
                <div className="space-y-2">
                  <div className="text-xs font-medium" style={{ color: colors.text.primary }}>
                    測定指標
                  </div>
                  {plan.metrics.map((metric, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: colors.text.secondary }}>
                        {metric.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm">
                          目標: <strong>+{metric.target}%</strong>
                        </span>
                        {metric.current !== undefined && (
                          <>
                            <span className="text-sm">
                              現在: <strong style={{ 
                                color: metric.current >= metric.target ? colors.support.success : colors.status.warning 
                              }}>
                                +{metric.current}%
                              </strong>
                            </span>
                            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full transition-all"
                                style={{ 
                                  width: `${(metric.current / metric.target) * 100}%`,
                                  backgroundColor: metric.current >= metric.target ? colors.support.success : colors.status.warning
                                }}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-3 flex justify-end gap-2">
                {plan.status === 'draft' && (
                  <>
                    <Button variant="secondary" size="small">編集</Button>
                    <Button variant="primary" size="small">開始</Button>
                  </>
                )}
                {plan.status === 'active' && (
                  <>
                    <Button variant="secondary" size="small">詳細</Button>
                    <Button variant="warning" size="small">中止</Button>
                  </>
                )}
                {plan.status === 'completed' && (
                  <Button variant="primary" size="small">結果レポート</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ABTestPlanner;
